import { Box, Icon, PropertyCard } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronRightFilled } from '@zillow/constellation-icons';

import { LoadingImage, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import {
  getFormattedAddress,
  getPhotoThumbUrl,
  getPropertyTypeLabel,
  getUnitSummary,
} from '../../../utils/flexiblePropertyCardUtils';

import { headerStyles, infoBlockStyles, overflowMenuStyles, photoContainerStyles } from '../styles';

import PropertyOverflowMenu from './PropertyOverflowMenu';

import type { AccountViewListingGroupDto, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listing: AccountViewListingGroupDto;
  isExpanded: boolean;
  isLoading?: boolean;
  onArchiveClick: (listingAlias: string, address: RentalManagerAddressDto) => void;
  onEditClick: () => void;
};

const FlexiblePropertyCardHeader = ({ listing, isExpanded, isLoading = false, onArchiveClick, onEditClick }: Props) => {
  const propertyTypeLabel = getPropertyTypeLabel(listing);
  const formattedAddress = getFormattedAddress(listing);
  const unitSummary = getUnitSummary(listing);
  const photoUrl = getPhotoThumbUrl(listing.photoKey);

  return (
    <Box id="ft-flexible-property-card-header" css={headerStyles} data-testid="FlexiblePropertyCard-Header">
      <Icon
        render={isExpanded ? <IconChevronDownFilled /> : <IconChevronRightFilled />}
        size="sm"
        aria-hidden={true}
        css={{ flexShrink: 0 }}
      />

      <Box css={photoContainerStyles} data-loading={isLoading || null}>
        {isLoading ? (
          <LoadingImage isLoading={true} loadingWidth="100%" loadingHeight="100%" />
        ) : (
          <PropertyCard.Photo
            src={photoUrl}
            alt={formattedAddress}
            fallbackImg={<PropertyCard.PhotoFallback />}
            css={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>

      <Box css={infoBlockStyles}>
        <LoadingText textStyle="body-xs-bold" isLoading={isLoading} loadingWidth="60px" css={{ color: 'text.subtle' }}>
          {propertyTypeLabel}
        </LoadingText>

        <LoadingParagraph textStyle="body-bold" isLoading={isLoading} loadingWidth="80%">
          {formattedAddress}
        </LoadingParagraph>

        <LoadingParagraph textStyle="body-sm" isLoading={isLoading} loadingWidth="120px" css={{ color: 'text.subtle' }}>
          {unitSummary}
        </LoadingParagraph>
      </Box>

      <Box css={overflowMenuStyles}>
        <PropertyOverflowMenu
          groupId={listing.groupId}
          listingAlias={listing.listingAlias}
          addressLabel={formattedAddress}
          address={listing.address}
          onArchiveClick={onArchiveClick}
          onEditClick={onEditClick}
        />
      </Box>
    </Box>
  );
};

export default FlexiblePropertyCardHeader;
